import { ROBOT_MODES } from 'utils/constants'
import type { IRobotInfo } from 'utils/interfaces'

export type CatalogOperationKind = 'calibrate' | 'reset' | 'service'
export type CatalogOperationPhase = 'idle' | 'sending' | 'active' | 'finishing'

export type CatalogOperationTarget =
  | {
      target: 'station_lot'
      station_id: string
      lot_id: number
      lot_model?: string
    }
  | {
      target: 'maintenance_point'
      maintenance_point_id: number
    }

export type RobotCatalogOperation = {
  phase: CatalogOperationPhase
  kind: CatalogOperationKind | null
  target: CatalogOperationTarget | null
  actionType: number | null
  effectiveMode: ROBOT_MODES | null
}

export const IDLE_CATALOG_OPERATION: RobotCatalogOperation = {
  phase: 'idle',
  kind: null,
  target: null,
  actionType: null,
  effectiveMode: null,
}

export type CatalogOperationRobotInfo = Pick<IRobotInfo, 'catalogOperation'>

function parseCatalogOperationTarget(raw: unknown): CatalogOperationTarget | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const target = raw as Record<string, unknown>
  if (target.target === 'station_lot' && target.station_id != null && target.lot_id != null) {
    return {
      target: 'station_lot',
      station_id: String(target.station_id),
      lot_id: Number(target.lot_id),
      ...(target.lot_model != null ? { lot_model: String(target.lot_model) } : {}),
    }
  }
  if (target.target === 'maintenance_point' && target.maintenance_point_id != null) {
    const maintenancePointId = Number(target.maintenance_point_id)
    if (maintenancePointId > 0) {
      return {
        target: 'maintenance_point',
        maintenance_point_id: maintenancePointId,
      }
    }
  }
  return null
}

/** Map API/WS `catalog_operation` JSON into client shape. */
export function mapCatalogOperationFromServer(raw: unknown): RobotCatalogOperation {
  if (raw === null || raw === undefined) {
    return IDLE_CATALOG_OPERATION
  }
  if (typeof raw !== 'object') {
    return IDLE_CATALOG_OPERATION
  }
  const data = raw as Record<string, unknown>
  const phase = data.phase
  if (phase !== 'idle' && phase !== 'sending' && phase !== 'active' && phase !== 'finishing') {
    return IDLE_CATALOG_OPERATION
  }

  const kindRaw = data.kind
  const kind = kindRaw === 'calibrate' || kindRaw === 'reset' || kindRaw === 'service' ? kindRaw : null

  const actionTypeRaw = data.action_type ?? data.actionType
  const actionType = actionTypeRaw != null && Number.isFinite(Number(actionTypeRaw)) ? Number(actionTypeRaw) : null

  const effectiveModeRaw = data.effective_mode ?? data.effectiveMode
  const effectiveMode =
    effectiveModeRaw != null && Number.isFinite(Number(effectiveModeRaw))
      ? (Number(effectiveModeRaw) as ROBOT_MODES)
      : null

  return {
    phase,
    kind: phase === 'idle' ? null : kind,
    target: parseCatalogOperationTarget(data.target),
    actionType,
    effectiveMode,
  }
}

/** Catalog operation from Redux — server snapshot only (`catalog_operation`). */
export function selectCatalogOperation(robotInfo: CatalogOperationRobotInfo | null | undefined): RobotCatalogOperation {
  return robotInfo?.catalogOperation ?? IDLE_CATALOG_OPERATION
}

export function isCatalogOperationVisible(operation: RobotCatalogOperation): boolean {
  return operation.phase !== 'idle' && operation.kind != null
}

export function isCatalogOperationBusy(operation: RobotCatalogOperation): boolean {
  return operation.phase === 'active' || operation.phase === 'sending'
}

export function isCatalogOperationKind(operation: RobotCatalogOperation, kind: CatalogOperationKind): boolean {
  return operation.kind === kind && operation.phase !== 'idle'
}
